// known issue https://github.com/date-fns/date-fns/issues/1677
import addMonths from 'date-fns/addMonths'
import differenceInDays from 'date-fns/differenceInDays'
import endOfMonth from 'date-fns/endOfMonth'
import endOfWeek from 'date-fns/endOfWeek'
import format from 'date-fns/format'
import startOfMonth from 'date-fns/startOfMonth'
import startOfWeek from 'date-fns/startOfWeek'
import startOfYear from 'date-fns/startOfYear'
import subDays from 'date-fns/subDays'
import subMonths from 'date-fns/subMonths'
import subWeeks from 'date-fns/subWeeks'
import { type Map, OrderedMap, type Record } from 'immutable'

import { type CurrencyCode, convertCurrency } from './currencies'

export interface CampaignCompany {
  clicks: number
  country: string
  id: string
  name: string
  views: number
}

type BudgetTypes = 'daily' | 'total'

type BudgetType = {
  id: BudgetTypes
  name: 'Daily' | ' Total'
}

export type CampaignDurations = '1month' | '2months' | '3months' | '6months' | '9months' | '12months' | 'custom'

export const campaignStatusesMap = OrderedMap({
  accepted: 'Accepted',
  cancelled: 'Cancelled',
  finished: 'Finished',
  review: 'Review',
  running: 'Running',
  paused: 'Paused',
  stopped: 'Stopped',
})

export const calculateCtr = (item: Record<CampaignCompany>) => {
  const clicks = item.get('clicks')
  const views = item.get('views') > 0 ? item.get('views') : 1
  return ((clicks / views) * 100).toFixed(3)
}

export const getInitialDateRange = (now = new Date()) => ({
  start: startOfMonth(now),
  end: endOfMonth(now),
})

export const getUrlRange = (start: Date | number, end: Date | number) =>
  [start, end].map(item => format(item, 'yyyyMMdd')).join('-')

export type CampaignDateRangePresetKey =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'thisMonth'
  | 'thisYear'
  | 'past7Days'
  | 'past30Days'
  | 'previousWeek'
  | 'previousMonth'
  | 'lifetime'

export type CampaignDateRangePreset = {
  key: CampaignDateRangePresetKey
  from: Date
  to: Date
}

// `now` is a parameter so a long-lived tab does not keep serving the ranges it computed on load.
export const campaignDateRangePresets = (now: Date = new Date()): CampaignDateRangePreset[] => [
  { key: 'today', from: now, to: now },
  { key: 'yesterday', from: subDays(now, 1), to: subDays(now, 1) },
  { key: 'thisWeek', from: startOfWeek(now, { weekStartsOn: 1 }), to: now },
  { key: 'thisMonth', from: startOfMonth(now), to: now },
  { key: 'thisYear', from: startOfYear(now), to: now },
  { key: 'past7Days', from: subDays(now, 6), to: now },
  { key: 'past30Days', from: subDays(now, 29), to: now },
  {
    key: 'previousWeek',
    from: startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }),
    to: endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }),
  },
  { key: 'previousMonth', from: startOfMonth(subMonths(now, 1)), to: endOfMonth(subMonths(now, 1)) },
  // local midnight: new Date('2000-01-01') is UTC and formats as 1999-12-31 west of Greenwich
  { key: 'lifetime', from: new Date(2000, 0, 1), to: now },
]

export const minBudgetPerTarget = (targets: number, currency?: CurrencyCode) =>
  convertCurrency(
    (targets => {
      if (targets < 2) {
        return 2
      } else if (targets < 10) {
        return 1.1
      } else if (targets < 20) {
        return 0.88
      } else if (targets < 50) {
        return 0.55
      }
      return 825 / targets / 30
    })(targets),
    'EUR',
    currency,
  )

export const maxBudgetPerTarget = (targets: number, currency?: CurrencyCode) =>
  convertCurrency(
    (targets => {
      if (targets < 100) {
        return 2.2
      } else if (targets < 200) {
        return (100 * 2.2 + (targets - 100) * 1.0) / targets
      } else if (targets < 500) {
        return (200 * 1.6 + (targets - 200) * 0.6) / targets
      }
      return (500 + (targets - 500) * 0.5) / targets
    })(targets),
    'EUR',
    currency,
  )

export const defaultBudgetPerTarget = (targets: number, currency?: CurrencyCode) => {
  const min = parseFloat(minBudget(targets, currency))
  const max = parseFloat(maxBudget(targets, currency))
  return ((min + Math.max(0.7 * max, min)) / 2).toFixed(2)
}

export const minBudget = (targets: number, currency?: CurrencyCode) =>
  targets > 50 ? (825 / 30).toFixed(2) : (targets * Number(minBudgetPerTarget(targets, currency))).toFixed(2)

export const maxBudget = (targets: number, currency?: CurrencyCode) =>
  (targets * Number(maxBudgetPerTarget(targets, currency))).toFixed(2)

export const getBudgetTypeKey = (budgetType: Map<string, BudgetType>) => budgetType?.keySeq().first()

export const getDurationKey = (duration?: OrderedMap<CampaignDurations, boolean>): CampaignDurations =>
  duration ? (duration.keySeq().first() as CampaignDurations) : '1month'

export const getTotalBudget = (
  budget: number,
  startDate: Date | string,
  endDate: Date | string,
  duration?: OrderedMap<CampaignDurations, boolean>,
) => {
  const durationKey = getDurationKey(duration)

  if (durationKey === 'custom') {
    const days = differenceInDays(new Date(endDate), new Date(startDate)) + 1
    return (days * budget).toFixed(2)
  }

  const monthsToAdd = parseInt(durationKey)
  const addedMonths = addMonths(new Date(startDate), monthsToAdd)
  const days = differenceInDays(addedMonths, new Date(startDate))
  return (budget * days).toFixed(2)
}

export const getFixedBudget = (
  budget: number,
  startDate: Date | string,
  endDate: Date | string,
  durationKey: CampaignDurations,
) => {
  if (durationKey === 'custom') {
    const days = differenceInDays(new Date(endDate), new Date(startDate)) + 1
    return budget / days
  }

  const monthsToAdd = parseInt(durationKey)
  const days = differenceInDays(addMonths(new Date(startDate), monthsToAdd), new Date(startDate))

  return budget / days
}

export const getMinTotalBudget = (
  _targetsNumber: number,
  currency: CurrencyCode,
  startDate: Date,
  endDate: Date,
  duration: OrderedMap<CampaignDurations, boolean>,
) => {
  const durationKey = getDurationKey(duration)

  return getTotalBudget(
    getFixedBudget(Number(convertCurrency(500, 'EUR', currency)), startDate, endDate, durationKey),
    startDate,
    endDate,
    duration,
  )
}

export const isCampaignNameValid = (name?: string | null) => (name ? !/[<>*'"?|;,\\]/.test(name) : false)
